import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonImg,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import React, { useEffect } from "react";
import { useApiProjects } from "@/api";
import { openExternalUrl } from "@/modules/shared/utils/browser.utils";
import { usePreferenceStore, useProjectsStore } from "@/core/stores";

type Props = {};

export function AboutPage({}: Props) {
  const { data: projects = [] } = useApiProjects();
  const currentProject = useProjectsStore((state) => state.currentProject);
  const nearbyLocationsCount = usePreferenceStore(
    (state) => state.locationsNearbyCount,
  );

  // Sync projects list to store
  useEffect(() => {
    if (projects.length > 0) {
      useProjectsStore.getState().setProjects(projects);
    }
  }, [projects]);

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    openExternalUrl("https://nidan.co.il");
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton />
          </IonButtons>

          <IonTitle>אודות</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {/* Header section with logo and name */}
        <div className="mb-0 flex flex-col items-center justify-center">
          <div
            className="w-40 h-40 flex items-center justify-center mb-1"
            style={{
              borderRadius: "var(--app-radius-md)",
              background: "rgba(255,255,255,0.08)",
            }}
          >
            <img
              src="/icon-192.png"
              alt="לוגו האפליקציה"
              className="h-36 w-36 object-contain"
              style={{
                borderRadius: "var(--app-radius-sm)",
              }}
            />
          </div>

          <IonText
            className="text-base font-bold tracking-normal"
            style={{
              color: "var(--ion-color-secondary)",
            }}
          >
            BeTogether
          </IonText>

          <IonText
            className="text-[0.8125rem] font-normal tracking-normal text-center leading-[1.5]"
            style={{
              color: "rgba(255,255,255,0.6)",
            }}
          >
            מציאת מקלטים קרובים
            <br />
            לביטחון ושלווה
          </IonText>
        </div>

        <div
          className="flex flex-col items-center text-center max-w-[400px] mx-auto"
          style={{ paddingInline: "var(--app-space-md)" }}
        >
          {/* Description */}
          <p
            className="leading-[1.6] mb-4"
            style={{ color: "var(--ion-text-color)" }}
          >
            שדה הכתובת מוזן באופן אוטומטי או לחילופין ניתן להקליד כתובת כלשהיא
            כדי לאתר מקלטים בסמוך אליה
          </p>

          <p
            className="leading-[1.6] mb-6"
            style={{ color: "var(--ion-text-color)" }}
          >
            מיקומי המקלטים בהתאם לדיוחי הרשות המקומית ובאחריותה
          </p>

          {/* Contact header */}
          <p
            className="leading-[1.6] mb-6"
            style={{ color: "var(--ion-text-color)" }}
          >
            להוספת מקלט שלא מופיע, או לכל שאלה תיפעולית אחרת יש לפנות אל:
          </p>

          {/* WhatsApp */}
          <div className="flex flex-col items-center mb-6">
            <h3 className="app-section-title mb-2">וואטסאפ:</h3>
            <a
              href="https://wa.me/972528720548"
              className="underline"
              style={{ color: "var(--ion-color-primary)" }}
            >
              https://wa.me/972528720548
            </a>
          </div>

          {/* Email */}
          <div className="flex flex-col items-center mb-6">
            <h3 className="app-section-title mb-2">מייל</h3>
            <a
              href="mailto:info@nidan.co.il"
              className="underline"
              style={{ color: "var(--ion-color-primary)" }}
            >
              info@nidan.co.il
            </a>
          </div>

          {/* Phone */}
          <div className="flex flex-col items-center mb-8">
            <h3 className="app-section-title mb-2">טלפון</h3>
            <a
              href="tel:052-8720548"
              className="underline"
              style={{ color: "var(--ion-color-primary)" }}
            >
              052-8720548
            </a>
          </div>

          {/* NIDAN Logo */}
          <div
            className="flex justify-center items-center mt-8"
            style={{
              paddingBlock: "var(--app-space-lg)",
            }}
          >
            <a
              href="https://nidan.co.il"
              onClick={handleLogoClick}
              className="inline-block cursor-pointer"
            >
              <IonImg
                src="/ndn-logo.png"
                alt="NIDAN Logo"
                className="w-[120px] h-auto max-w-full"
              />
            </a>
          </div>

          {/* Project select */}
          <div className="w-72 my-6">
            <IonList lines="none">
              <IonItem>
                <IonSelect
                  label="פרויקט"
                  labelPlacement="fixed"
                  interface="action-sheet"
                  interfaceOptions={{ header: "בחר פרויקט" }}
                  cancelText="ביטול"
                  placeholder="בחר פרויקט"
                  value={currentProject?.id}
                  onIonChange={(e) => {
                    const selectedProjectId = e.detail.value;
                    const selectedProject = projects.find(
                      (p) => p.id === selectedProjectId,
                    );
                    if (selectedProject) {
                      useProjectsStore
                        .getState()
                        .setCurrentProject(selectedProject);
                    }
                  }}
                >
                  {projects.map((project) => (
                    <IonSelectOption key={project.id} value={project.id}>
                      {project.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              <IonItem>
                <IonInput
                  label="מספר מקומות קרובים להצגה"
                  labelPlacement="start"
                  inputmode="numeric"
                  placeholder=".."
                  type="number"
                  min="5"
                  value={nearbyLocationsCount.toString()}
                  onIonInput={(e) => {
                    const value = parseInt(e.detail.value || "5", 10);
                    if (!isNaN(value) && value >= 5) {
                      usePreferenceStore
                        .getState()
                        .setLocationsNearbyCount(value);
                    }
                  }}
                />
              </IonItem>
            </IonList>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
